import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { FormProvider, useForm } from 'react-hook-form';
import { Divider, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import ReCAPTCHA from 'react-google-recaptcha';
import GoogleLoginButton from './GoogleLogin/GoogleLoginButton';
import MicrosoftLoginButton from './MicrosoftLogin/MicrosoftLoginButton';
import '../../Styles/login.scss';
import CustomButton from '../../Components/Inputs/CustomButton';
import FilledLogo from '../../Components/Logos/FilledLogo';
import {
  CenteredFlexBox,
  ColumnFlexBox,
  FullSizeCenteredColumn,
} from '../../Components/FlexBox';
import ThemeFab from '../../Components/Button/ThemeFab';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import Fab from '../../Components/Navbar/Fab';

const useStyles = makeStyles({
  img: {
    height: '100%',
    width: '100%',
  },
  loginForm: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    gap: 6,
    marginTop: 5,
  },
});

const LoginFormPage = function LoginFormPage({
  widget,
  onSubmit,
  needToChangePassword = false,
}) {
  const intl = useIntl();
  const classes = useStyles();
  const methods = useForm();
  const theme = useTheme();
  const { API_URL, PROVIDER } = window.databeasy || {};
  const { AUTH_SCHEME, RECAPTCHA } = window.databeasy || {};

  const recaptchaRef = useRef();
  const [loginActive, setLoginActive] = useState(false);

  const redirectURI = `${window.location.protocol}//${window.location.host}/`;

  const googleLoginUrl = `${API_URL}Auth/externalLogin?provider=Google&authScheme=${AUTH_SCHEME}&returnUrl=${redirectURI}`;

  const microsoftLoginUrl = `${API_URL}Auth/externalLogin?provider=Microsoft&authScheme=${AUTH_SCHEME}&returnUrl=${redirectURI}`;

  const onRecaptchaVerifySubmit = (data) =>
    onSubmit(data, recaptchaRef.current.getValue());

  return (
    <div
      className="login"
      style={{ display: 'flex', width: '100%', height: '100%' }}
    >
      <div className="left" style={{ width: '50%' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginTop: '10%',
            padding: '36px',
            height: '100%',
            width: '100%',
            gap: '16px',
          }}
        >
          <FilledLogo width="600px" height="160px" />
          <Typography
            sx={{
              color: '#fff',
              fontWeight: 400,
              fontSize: '3rem',
              whiteSpace: 'pre-wrap',
            }}
          >
            {intl.messages.SLOGAN}
          </Typography>
        </div>
      </div>
      <div
        className="right"
        style={{
          width: '50%',
          backgroundColor: theme.palette.background.default,
          transition: 'background-color 0.5s ease-in-out',
        }}
      >
        <CenteredFlexBox gap="16px" position="absolute" top={16} right={16}>
          <Fab title="HELP" href="https://docs.databeasy.it/">
            <HelpOutlineRoundedIcon />
          </Fab>
          <ThemeFab />
        </CenteredFlexBox>
        <FullSizeCenteredColumn alignItems="center" overflow="auto">
          <ColumnFlexBox width="50%">
            <FormProvider {...methods}>
              <form
                onSubmit={methods.handleSubmit(onRecaptchaVerifySubmit)}
                style={{ display: 'contents' }}
              >
                <div className={classes.loginForm}>
                  {widget}
                  <ReCAPTCHA
                    style={{ width: '100%' }}
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA}
                    onChange={() => setLoginActive(true)}
                  />
                  <CustomButton
                    type="submit"
                    data-testid="login-submit"
                    styles={{
                      mb: '24px',
                      height: '50px',
                      mt: '8px',
                    }}
                    disabled={!loginActive}
                    label={intl.messages.LOGIN}
                  />
                </div>
              </form>
            </FormProvider>

            <CustomButton
              type="submit"
              styles={{
                mb: '24px',
                height: '50px',
                mt: '8px',
              }}
              label={intl.messages.LOGIN_PASSKEY}
              onClick={() => onSubmit('passkey')}
            />
            {!needToChangePassword && (
              <>
                <Divider
                  sx={{
                    '&.MuiDivider-root': {
                      '&::before': {
                        borderTop: '1px solid #d6d6d6',
                      },
                      '&::after': {
                        borderTop: '1px solid #d6d6d6',
                      },
                    },
                    mb: '24px',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#a2a2a2' }}>
                    {intl.messages.OR}
                  </Typography>
                </Divider>
                {PROVIDER === 'GOOGLE' && (
                  <GoogleLoginButton
                    loginUrl={googleLoginUrl}
                    label={intl.messages.GOOGLELOGIN}
                  />
                )}
                {PROVIDER === 'MICROSOFT' && (
                  <MicrosoftLoginButton
                    loginUrl={microsoftLoginUrl}
                    label={intl.messages.MICROSOFTLOGIN}
                  />
                )}
              </>
            )}
          </ColumnFlexBox>
        </FullSizeCenteredColumn>
      </div>
    </div>
  );
};

LoginFormPage.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  widget: PropTypes.object.isRequired,
  needToChangePassword: PropTypes.bool,
};

export default LoginFormPage;
